import { useState } from 'react';
import { fetchDownloadProjectContract } from '@/service/api/project';
import { formatDate } from './utils';

interface ContractTabProps {
  project: Api.Project.ProjectDetailResponse;
}

export default function ContractTab({ project }: ContractTabProps) {
  const contract = project.contract;
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!project.id) return;
    setDownloading(true);
    try {
      if (contract?.fully_executed_asset?.access_url) {
        window.open(contract.fully_executed_asset.access_url, '_blank');
      } else if (contract?.platform_draft_asset?.access_url) {
        window.open(contract.platform_draft_asset.access_url, '_blank');
      } else {
        const res = await fetchDownloadProjectContract(project.id);
        if (res?.fully_executed_asset?.access_url) {
          window.open(res.fully_executed_asset.access_url, '_blank');
        } else if (res?.platform_draft_asset?.access_url) {
          window.open(res.platform_draft_asset.access_url, '_blank');
        } else {
          alert('未能获取到合同下载链接');
        }
      }
    } catch (err) {
      alert('下载合同失败: ' + (err instanceof Error ? err.message : '网络错误'));
    } finally {
      setDownloading(false);
    }
  };

  if (!contract) {
    return (
      <div className="py-8 text-center text-foreground-400 text-sm">
        <i className="ri-file-unknow-line text-2xl block mb-2 text-foreground-300"></i>
        暂无关联合同归档记录
      </div>
    );
  }

  const isExecuted = contract.status === 'FULLY_EXECUTED' || Boolean(contract.fully_executed_asset);
  const isOwnerSigned = Boolean(contract.owner_signed_asset);

  return (
    <div>
      <h3 className="font-semibold text-foreground-950 text-sm mb-2">合同归档</h3>
      <p className="text-xs text-foreground-500 mb-6">请下载合同文件，完成线下签章后上传扫描件完成归档</p>

      <div className="space-y-4">
        <div className="p-5 rounded-xl bg-gray-50 border border-gray-200/60">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <i className="ri-file-pdf-line text-primary-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground-900">
                  《{project.title || project.source_application?.title || '项目合作协议'}》发行合作协议
                </p>
                <p className="text-xs text-foreground-400">
                  生成于 {formatDate(contract.created_at)} · {isExecuted ? '已全归档' : (isOwnerSigned ? '已回传 (审核中)' : '待签章')}
                </p>
              </div>
            </div>
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="px-4 py-2 rounded-lg bg-primary-500 text-foreground-950 text-xs font-medium hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {downloading ? '准备中...' : '下载合同'}
            </button>
          </div>

          <div className="border-t border-background-200/70 pt-4">
            <p className="text-xs font-medium text-foreground-700 mb-2">上传签章扫描件</p>
            {isExecuted ? (
              <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-xs text-green-700 flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-green-600 text-base"></i>
                合同已双方向完成签署归档并生效。
              </div>
            ) : (
              <div className="border-2 border-dashed border-background-300/60 rounded-xl p-6 text-center hover:border-primary-300/60 transition-colors cursor-pointer">
                <i className="ri-upload-cloud-2-line text-xl text-foreground-400 mb-1 block"></i>
                <p className="text-xs text-foreground-500">点击或拖拽上传已签章的合同扫描件</p>
                <p className="text-xs text-foreground-400">支持 PDF 格式，大小不超过 20MB</p>
              </div>
            )}
          </div>
        </div>

        {contract.review_records && contract.review_records.length > 0 && (
          <div className="mt-4 p-4 rounded-xl bg-gray-50 border border-gray-200/60">
            <p className="text-xs font-semibold text-foreground-900 mb-2">审核历史</p>
            <div className="space-y-2">
              {contract.review_records.map((rec) => (
                <div key={rec.id} className="text-xs text-foreground-600 flex justify-between">
                  <span>{formatDate(rec.reviewed_at || rec.created_at)} 审核结果: {rec.result}</span>
                  <span className="text-foreground-400">{rec.review_remark}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
